import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Plus, Search, Edit, Trash2, Loader2, X, Download, Sparkles } from 'lucide-react'
import { apiGet, apiDelete, apiPost, formatMXN } from '../lib/api'

interface ProductosData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: Array<{
    id: string
    sku: string
    nombre: string
    precio: number
    precioOferta: number | null
    stock: number
    enOferta: boolean
    destacado: boolean
    imagen: string
    brandId: string
    brandNombre: string
    categoriaId: string
    categoriaNombre: string
  }>
}

interface CategoriasData {
  data: Array<{ id: string; nombre: string }>
}

interface MarcasData {
  data: Array<{ id: string; nombre: string }>
}

type ToggleField =
  | 'is_active'
  | 'inhibit_purchase'
  | 'is_featured'
  | 'is_on_sale'
  | 'is_new'
  | 'is_hot'

type AssignMode = 'category' | 'brand'

interface BulkAssignResponse {
  ok: boolean
  updated: number
}

interface BulkToggleResponse {
  ok: boolean
  updated: number
  field: ToggleField
  value: boolean
}

function stockBadge(stock: number) {
  if (stock === 0) return <Badge variant="danger">Agotado</Badge>
  if (stock < 5) return <Badge variant="warning">Stock bajo ({stock})</Badge>
  return <span className="text-sm font-medium text-ink-700">{stock}</span>
}

export function Productos() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [brandId, setBrandId] = useState('')

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [assignMode, setAssignMode] = useState<AssignMode | null>(null)
  const [assignValue, setAssignValue] = useState('')
  const [bulkError, setBulkError] = useState<string | null>(null)
  const [bulkInfo, setBulkInfo] = useState<string | null>(null)

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/productos/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['productos'] })
    },
  })

  const autoGenRecsMut = useMutation<
    { ok: boolean; pairs: number; inserted: number; updated: number },
    Error
  >({
    mutationFn: () => apiPost('/api/admin/recomendaciones/auto-generate'),
    onSuccess: (res) => {
      setBulkInfo(
        `Recomendaciones generadas: ${res.pairs} pares analizados, ${res.inserted} nuevas, ${res.updated} actualizadas.`,
      )
      setBulkError(null)
    },
    onError: (e) => {
      setBulkError(e.message)
      setBulkInfo(null)
    },
  })

  const bulkAssignMut = useMutation<
    BulkAssignResponse,
    Error,
    { productIds: string[]; categoryId?: string; brandId?: string }
  >({
    mutationFn: (body) => apiPost<BulkAssignResponse>('/api/admin/productos/bulk-assign', body),
    onSuccess: (res) => {
      setBulkInfo(`${res.updated} producto(s) actualizado(s).`)
      setBulkError(null)
      setAssignMode(null)
      setAssignValue('')
      setSelected(new Set())
      qc.invalidateQueries({ queryKey: ['productos'] })
    },
    onError: (e) => {
      setBulkError(e.message)
      setBulkInfo(null)
    },
  })

  const bulkToggleMut = useMutation<
    BulkToggleResponse,
    Error,
    { productIds: string[]; field: ToggleField; value: boolean }
  >({
    mutationFn: (body) => apiPost<BulkToggleResponse>('/api/admin/productos/bulk-toggle', body),
    onSuccess: (res) => {
      setBulkInfo(`${res.updated} producto(s) actualizado(s).`)
      setBulkError(null)
      setSelected(new Set())
      qc.invalidateQueries({ queryKey: ['productos'] })
    },
    onError: (e) => {
      setBulkError(e.message)
      setBulkInfo(null)
    },
  })

  function onDelete(id: string, nombre: string) {
    if (window.confirm(`¿Eliminar producto "${nombre}"?\nSe marcará como inactivo.`)) {
      deleteMut.mutate(id)
    }
  }

  const query = new URLSearchParams()
  query.set('page', String(page))
  if (searchTerm) query.set('q', searchTerm)
  if (categoryId) query.set('categoryId', categoryId)
  if (brandId) query.set('brandId', brandId)

  const { data, isLoading, isFetching } = useQuery<ProductosData>({
    queryKey: ['productos', page, searchTerm, categoryId, brandId],
    queryFn: () => apiGet(`/api/admin/productos?${query.toString()}`),
    placeholderData: (prev) => prev,
  })

  const { data: categorias } = useQuery<CategoriasData>({
    queryKey: ['admin-categorias-min'],
    queryFn: () => apiGet('/api/admin/categorias?page=1'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: marcas } = useQuery<MarcasData>({
    queryKey: ['admin-marcas-min'],
    queryFn: () => apiGet('/api/admin/marcas?page=1'),
    staleTime: 5 * 60 * 1000,
  })

  const pageIds = useMemo(() => data?.data.map((p) => p.id) ?? [], [data])
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  const someOnPageSelected =
    !allOnPageSelected && pageIds.some((id) => selected.has(id))

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAllOnPage() {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) {
        for (const id of pageIds) next.delete(id)
      } else {
        for (const id of pageIds) next.add(id)
      }
      return next
    })
  }

  function clearSelection() {
    setSelected(new Set())
    setAssignMode(null)
    setAssignValue('')
    setBulkError(null)
    setBulkInfo(null)
  }

  function openAssign(mode: AssignMode) {
    setAssignMode(mode)
    setAssignValue('')
    setBulkError(null)
    setBulkInfo(null)
  }

  function submitAssign() {
    if (!assignMode) return
    if (!assignValue) {
      setBulkError(
        assignMode === 'category' ? 'Selecciona una categoría' : 'Selecciona una marca',
      )
      return
    }
    const productIds = Array.from(selected)
    if (productIds.length === 0) return
    bulkAssignMut.mutate({
      productIds,
      ...(assignMode === 'category' ? { categoryId: assignValue } : { brandId: assignValue }),
    })
  }

  function applyToggle(field: ToggleField, value: boolean) {
    const productIds = Array.from(selected)
    if (productIds.length === 0) return
    bulkToggleMut.mutate({ productIds, field, value })
  }

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchTerm(q.trim())
  }

  function clearFilters() {
    setQ('')
    setSearchTerm('')
    setCategoryId('')
    setBrandId('')
    setPage(1)
  }

  function goToDetail(id: string) {
    navigate({ to: '/productos/$id', params: { id } })
  }

  function goToNew() {
    navigate({ to: '/productos/nuevo' })
  }

  function exportCsv() {
    const params = new URLSearchParams()
    params.set('format', 'csv')
    if (categoryId) params.set('categoryId', categoryId)
    if (brandId) params.set('brandId', brandId)
    window.location.href = `/api/admin/productos/export?${params.toString()}`
  }

  const hasFilters = searchTerm || categoryId || brandId
  const selectionCount = selected.size
  const bulkBusy = bulkAssignMut.isPending || bulkToggleMut.isPending

  return (
    <>
      <Topbar
        title="Productos"
        subtitle={
          data ? `${data.total.toLocaleString('es-MX')} productos en catálogo` : 'Cargando…'
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('¿Generar recomendaciones automáticas a partir de pedidos? Puede tardar unos segundos.')) {
                  autoGenRecsMut.mutate()
                }
              }}
              disabled={autoGenRecsMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
              title="Genera 'compraron también' analizando productos comprados juntos en pedidos"
            >
              {autoGenRecsMut.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Sparkles className="size-3.5" />
              )}
              Auto-generar recomendaciones
            </button>
            <button
              type="button"
              onClick={goToNew}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" /> Nuevo
            </button>
          </>
        }
      />
      <div className="p-6">
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[260px] flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por SKU o nombre…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white py-2 pl-3 pr-8 text-sm outline-none focus:border-brand-500"
          >
            <option value="">Todas las categorías</option>
            {categorias?.data.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
          <select
            value={brandId}
            onChange={(e) => {
              setBrandId(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white py-2 pl-3 pr-8 text-sm outline-none focus:border-brand-500"
          >
            <option value="">Todas las marcas</option>
            {marcas?.data.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <X className="size-3.5" /> Limpiar
            </button>
          )}
          <button
            type="button"
            onClick={exportCsv}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            title="Exportar productos a CSV (respeta filtros)"
          >
            <Download className="size-3.5" /> Exportar CSV
          </button>
        </form>

        {selectionCount > 0 && (
          <div className="mb-3 rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-brand-900">
                {selectionCount} seleccionado(s)
              </span>
              <span className="mx-1 h-5 w-px bg-brand-200" />
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => openAssign('category')}
                className="rounded-md border border-brand-300 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50"
              >
                Asignar categoría
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => openAssign('brand')}
                className="rounded-md border border-brand-300 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50"
              >
                Asignar marca
              </button>
              <span className="mx-1 h-5 w-px bg-brand-200" />
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('is_active', true)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Activar
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('is_active', false)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Desactivar
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('inhibit_purchase', true)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Inhibir compra
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('inhibit_purchase', false)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Permitir compra
              </button>
              <span className="mx-1 h-5 w-px bg-brand-200" />
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('is_featured', true)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Marcar destacado
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('is_featured', false)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Quitar destacado
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('is_on_sale', true)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Marcar oferta
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('is_on_sale', false)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Quitar oferta
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('is_new', true)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Marcar nuevo
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => applyToggle('is_hot', true)}
                className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
              >
                Marcar hot
              </button>
              <div className="ml-auto flex items-center gap-2">
                {bulkBusy && <Loader2 className="size-4 animate-spin text-brand-600" />}
                <button
                  type="button"
                  onClick={clearSelection}
                  className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100"
                >
                  <X className="size-3.5" /> Limpiar
                </button>
              </div>
            </div>

            {(bulkError || bulkInfo) && (
              <div
                className={`mt-2 rounded-md px-3 py-1.5 text-xs ${
                  bulkError
                    ? 'bg-red-100 text-red-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}
              >
                {bulkError ?? bulkInfo}
              </div>
            )}
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando productos…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label="Seleccionar todos"
                          checked={allOnPageSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someOnPageSelected
                          }}
                          onChange={toggleAllOnPage}
                          className="size-4 cursor-pointer rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                        />
                      </th>
                      <th className="px-4 py-3 font-medium">Producto</th>
                      <th className="px-4 py-3 font-medium">SKU</th>
                      <th className="px-4 py-3 font-medium">Categoría / Marca</th>
                      <th className="px-4 py-3 font-medium">Stock</th>
                      <th className="px-4 py-3 font-medium text-right">Precio</th>
                      <th className="w-20 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((p) => {
                      const checked = selected.has(p.id)
                      return (
                        <tr
                          key={p.id}
                          onClick={() => goToDetail(p.id)}
                          className={`cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50 ${
                            checked ? 'bg-brand-50/40' : ''
                          }`}
                        >
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              aria-label={`Seleccionar ${p.nombre}`}
                              checked={checked}
                              onChange={() => toggleRow(p.id)}
                              className="size-4 cursor-pointer rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-ink-100 text-xs font-bold text-brand-600">
                                {p.imagen ? (
                                  <img
                                    src={p.imagen}
                                    alt=""
                                    className="size-10 rounded-lg object-cover"
                                    onError={(e) => {
                                      ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                                    }}
                                  />
                                ) : (
                                  'M'
                                )}
                              </div>
                              <div>
                                <div className="line-clamp-2 max-w-md font-medium text-ink-900">
                                  {p.nombre}
                                </div>
                                <div className="mt-0.5 flex gap-1">
                                  {p.destacado && <Badge variant="info">Destacado</Badge>}
                                  {p.enOferta && <Badge variant="warning">Oferta</Badge>}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-ink-700">{p.sku}</td>
                          <td className="px-4 py-3">
                            <div className="text-xs text-ink-700">
                              {p.categoriaNombre || '—'}
                            </div>
                            <div className="text-xs text-ink-500">{p.brandNombre || '—'}</div>
                          </td>
                          <td className="px-4 py-3">{stockBadge(p.stock)}</td>
                          <td className="px-4 py-3 text-right">
                            {p.enOferta && p.precioOferta ? (
                              <>
                                <div className="text-xs text-ink-400 line-through">
                                  {formatMXN(p.precio)}
                                </div>
                                <div className="font-semibold text-ink-900">
                                  {formatMXN(p.precioOferta)}
                                </div>
                              </>
                            ) : (
                              <div className="font-semibold text-ink-900">
                                {formatMXN(p.precio)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate({
                                    to: '/productos/$id/editar',
                                    params: { id: p.id },
                                  })
                                }
                                className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                                title="Editar"
                              >
                                <Edit className="size-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => onDelete(p.id, p.nombre)}
                                disabled={deleteMut.isPending}
                                className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
                                title="Eliminar"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong>{' '}
                  ({data.total.toLocaleString('es-MX')} productos)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {assignMode && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => {
            if (!bulkAssignMut.isPending) {
              setAssignMode(null)
              setAssignValue('')
            }
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink-900">
                {assignMode === 'category' ? 'Asignar categoría' : 'Asignar marca'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setAssignMode(null)
                  setAssignValue('')
                }}
                disabled={bulkAssignMut.isPending}
                className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-50"
                aria-label="Cerrar"
              >
                <X className="size-4" />
              </button>
            </div>
            <p className="mb-3 text-sm text-ink-600">
              Se aplicará a {selectionCount} producto(s) seleccionado(s).
            </p>
            <select
              value={assignValue}
              onChange={(e) => setAssignValue(e.target.value)}
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-3 pr-8 text-sm outline-none focus:border-brand-500"
            >
              <option value="">
                {assignMode === 'category'
                  ? 'Selecciona una categoría…'
                  : 'Selecciona una marca…'}
              </option>
              {assignMode === 'category' &&
                categorias?.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              {assignMode === 'brand' &&
                marcas?.data.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
            </select>
            {bulkError && (
              <div className="mt-2 rounded-md bg-red-100 px-3 py-1.5 text-xs text-red-700">
                {bulkError}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAssignMode(null)
                  setAssignValue('')
                }}
                disabled={bulkAssignMut.isPending}
                className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitAssign}
                disabled={bulkAssignMut.isPending || !assignValue}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {bulkAssignMut.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Asignar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Productos
